import { ArrowRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  acceptInvite,
  attachInviteByCode,
  resolveInvite,
  sendEmailOTP,
  setSessionToken,
  signInWithEmailOTP,
} from "../api/client";
import { AuthFrame } from "./Invite";

// /sign-in — two lanes:
//   • "Email me a code": Better Auth email-OTP for returning portal users
//     (send-verification-otp → sign-in/email-otp; bearer comes back in the
//     set-auth-token header).
//   • "I have an invite code": manual entry of the (email, 8-letter code)
//     pair against POST /api/v1/invite/accept — same redemption as /invite/:code.

type Mode = "otp" | "invite";

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
const cleanEmail = (s: string) => s.trim().toLowerCase();
// Letter-only invite alphabet (drops I/O); display form is XXXX-XXXX.
const normalizeInviteCode = (s: string) =>
  s
    .toUpperCase()
    .replace(/[\s-]/g, "")
    .replace(/[^ABCDEFGHJKLMNPQRSTUVWXYZ]/g, "")
    .slice(0, 8);

export function SignIn() {
  const [mode, setMode] = useState<Mode>("otp");

  return (
    <AuthFrame>
      <div className="luxe-panel rounded-card p-7 shadow-lift">
        <div className="text-[17px] font-semibold">Sign in</div>
        <p className="mt-1 text-[14px] text-ink-soft">
          Access the events your agency runs for you.
        </p>

        <div className="mt-5 flex rounded-input border border-hairline bg-paper/70 p-1 text-[13px] font-medium">
          <ModeTab active={mode === "otp"} onClick={() => setMode("otp")}>
            Email me a code
          </ModeTab>
          <ModeTab active={mode === "invite"} onClick={() => setMode("invite")}>
            I have an invite code
          </ModeTab>
        </div>

        <div className="mt-5">{mode === "otp" ? <OtpPane /> : <InvitePane />}</div>
      </div>
    </AuthFrame>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-[8px] px-3 py-1.5 transition-colors ${
        active ? "bg-card text-ink shadow-card" : "text-ink-soft hover:bg-white/8 hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

// ----- Returning users: email OTP ------------------------------------------

function OtpPane() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSend(e: React.FormEvent) {
    e.preventDefault();
    if (!isValidEmail(email) || busy) return;
    setBusy(true);
    setError(null);
    try {
      await sendEmailOTP(cleanEmail(email));
      setStep("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send the code");
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    if (otp.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const token = await signInWithEmailOTP(cleanEmail(email), otp);
      setSessionToken(token);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wrong code — please try again");
      setOtp("");
      setBusy(false);
    }
  }

  if (step === "email") {
    return (
      <form onSubmit={onSend} className="flex flex-col gap-3">
        <label className="eyebrow" htmlFor="otp-email">
          Email
        </label>
        <input
          id="otp-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@company.com"
          className="field"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        {error && <p className="text-[13px] text-rose-700">{error}</p>}
        <button type="submit" className="btn-primary" disabled={!isValidEmail(email) || busy}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <>Send code <ArrowRight size={16} /></>}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={onVerify} className="flex flex-col gap-3">
      <p className="text-[14px] text-ink-soft">
        We sent a 6-digit code to <span className="font-medium text-ink">{cleanEmail(email)}</span>.
      </p>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={6}
        placeholder="••••••"
        className="field text-center text-[20px] tracking-[0.4em]"
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
        autoFocus
      />
      {error && <p className="text-[13px] text-rose-700">{error}</p>}
      <button type="submit" className="btn-primary" disabled={otp.length !== 6 || busy}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <>Verify & continue <ArrowRight size={16} /></>}
      </button>
      <button
        type="button"
        className="btn-quiet self-center"
        onClick={() => {
          setStep("email");
          setOtp("");
          setError(null);
        }}
      >
        Use a different email
      </button>
    </form>
  );
}

// ----- First visit without the link: manual invite redemption ---------------

function InvitePane() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [otp, setOtp] = useState("");
  const [otpRequired, setOtpRequired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = isValidEmail(email) && code.length === 8 && !busy;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const normalizedEmail = cleanEmail(email);
      const resolved = await resolveInvite(code, normalizedEmail);
      if (!resolved.valid || resolved.revoked || resolved.expired) {
        throw new Error("This invitation is no longer active.");
      }
      if (resolved.bearerRefused) {
        await sendEmailOTP(normalizedEmail);
        setOtpRequired(true);
        setBusy(false);
        return;
      }
      const token = await acceptInvite(normalizedEmail, code);
      setSessionToken(token);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn't work — check the code and email");
      setBusy(false);
    }
  }

  async function onVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (otp.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const token = await signInWithEmailOTP(cleanEmail(email), otp);
      setSessionToken(token);
      await attachInviteByCode(code);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wrong code — please try again");
      setOtp("");
      setBusy(false);
    }
  }

  if (otpRequired) {
    return (
      <form onSubmit={onVerifyOtp} className="flex flex-col gap-3">
        <p className="text-[14px] text-ink-soft">
          We sent a 6-digit sign-in code to <span className="font-medium text-ink">{cleanEmail(email)}</span>.
        </p>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="••••••"
          className="field text-center text-[20px] tracking-[0.4em]"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          autoFocus
        />
        {error && <p className="text-[13px] text-rose-700">{error}</p>}
        <button type="submit" className="btn-primary" disabled={otp.length !== 6 || busy}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <>Verify & continue <ArrowRight size={16} /></>}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <label className="eyebrow" htmlFor="invite-email">
        Email
      </label>
      <input
        id="invite-email"
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder="you@company.com"
        className="field"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <label className="eyebrow mt-1" htmlFor="invite-code">
        Invite code
      </label>
      <input
        id="invite-code"
        type="text"
        autoComplete="off"
        placeholder="XXXX-XXXX"
        className="field text-center text-[17px] font-semibold uppercase tracking-[0.25em]"
        value={code}
        onChange={(e) => setCode(normalizeInviteCode(e.target.value))}
        maxLength={9}
      />
      <p className="text-[12px] text-ink-faint">The 8-letter code from your invitation email.</p>
      {error && <p className="text-[13px] text-rose-700">{error}</p>}
      <button type="submit" className="btn-primary" disabled={!canSubmit}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <>Verify & continue <ArrowRight size={16} /></>}
      </button>
    </form>
  );
}
